import React, { useState, useEffect } from 'react';
import { useInvoice } from '../context/InvoiceContext';
import { recurringExpenseService, creditService } from '../services/api';
import useEscapeKey from '../utils/useEscapeKey';
import { formatCurrency } from '../utils/formatCurrency';
import Select from './Select';

const FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half Yearly' },
  { value: 'yearly', label: 'Yearly' }
];

const InvoiceUpload = ({ onClose, onRecurringCreated }) => {
  const { uploadInvoice, showNotification, currentUser } = useInvoice();
  useEscapeKey(onClose);
  const [isRecurring, setIsRecurring] = useState(false);
  const [gstIncluded, setGstIncluded] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('standard');
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    vendorName: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    amount: '',
    notes: '',
    frequency: 'monthly'
  });
  const [preview, setPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [liveBalance, setLiveBalance] = useState(null);

  useEffect(() => {
    if (currentUser?.id) {
      creditService.getTransactions(currentUser.id)
        .then(data => { if (data.balance !== undefined) setLiveBalance(parseFloat(data.balance)); })
        .catch(() => {});
    }
  }, [currentUser?.id]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      if (!['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'].includes(selectedFile.type)) {
        alert('Please upload only PNG, JPG images or PDF files');
        return;
      }
      // Validate file size (10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
      // Create preview (only for images)
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setPreview(null); // No preview for PDF
      }
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (isRecurring) {
      try {
        await recurringExpenseService.create({
          vendorName: formData.vendorName,
          amount: formData.amount,
          notes: formData.notes,
          frequency: formData.frequency,
          startDate: formData.invoiceDate,
          gstIncluded
        });
        showNotification('Recurring expense created', 'success');
        if (onRecurringCreated) onRecurringCreated();
        onClose();
      } catch (error) {
        showNotification(error.message || 'Failed to create recurring expense', 'error');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('vendorName', formData.vendorName);
    uploadData.append('invoiceDate', formData.invoiceDate);
    uploadData.append('amount', formData.amount);
    uploadData.append('notes', formData.notes);
    uploadData.append('gstIncluded', gstIncluded);
    uploadData.append('paymentMethod', paymentMethod);

    const success = await uploadInvoice(uploadData);
    setIsLoading(false);

    if (success) {
      if (paymentMethod === 'credit_balance' && liveBalance !== null) {
        setLiveBalance(liveBalance - parseFloat(formData.amount));
      }
      setPaymentMethod('standard');
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex-between mb-lg">
          <h3>{isRecurring ? 'Add Recurring Expense' : 'Add Expense'}</h3>
          <button onClick={onClose} className="btn btn-secondary btn-sm">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Recurring toggle */}
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
                style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary)' }}
              />
              <span className="form-label" style={{ margin: 0 }}>Recurring Expense</span>
            </label>
          </div>

          {!isRecurring && (
          <div className="form-group">
            <label className="form-label">Invoice File (Optional, PNG/JPG/PDF)</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,application/pdf"
              onChange={handleFileChange}
              className="form-input"
            />
            {preview && (
              <img
                src={preview}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  marginTop: 'var(--spacing-sm)',
                  borderRadius: 'var(--radius-sm)'
                }}
              />
            )}
            {file && file.type === 'application/pdf' && (
              <div style={{
                marginTop: 'var(--spacing-sm)',
                padding: 'var(--spacing-sm)',
                backgroundColor: 'var(--color-gray-50)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-sm)'
              }}>
                📄 {file.name}
              </div>
            )}
          </div>
          )}

          <div className="form-group">
            <label className="form-label">Vendor Name *</label>
            <input
              type="text"
              name="vendorName"
              className="form-input"
              value={formData.vendorName}
              onChange={handleChange}
              placeholder={isRecurring ? 'e.g., Netflix, Rent, etc.' : 'e.g., Amazon, Uber, etc.'}
              required
            />
          </div>

          {isRecurring && (
            <div className="form-group">
              <label className="form-label">Frequency *</label>
              <Select
                value={formData.frequency}
                onChange={v => setFormData({ ...formData, frequency: v })}
                options={FREQUENCY_OPTIONS}
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{isRecurring ? 'Start Date *' : 'Invoice Date *'}</label>
            <input
              type="date"
              name="invoiceDate"
              className="form-input"
              value={formData.invoiceDate}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Amount *</label>
            <input
              type="number"
              name="amount"
              className="form-input"
              value={formData.amount}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="0.00"
              required
            />
          </div>

          {!isRecurring && (
          <div className="form-group">
            {(() => {
              const balance = liveBalance !== null ? liveBalance : parseFloat(currentUser?.creditBalance || 0);
              return (
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={paymentMethod === 'credit_balance'}
                onChange={e => setPaymentMethod(e.target.checked ? 'credit_balance' : 'standard')}
                style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary)' }}
              />
              <span className="form-label" style={{ margin: 0 }}>Paid from Credit Balance</span>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginLeft: 'var(--spacing-xs)' }}>
                ₹{balance.toFixed(2)}
              </span>
            </label>
              );
            })()}
            {paymentMethod === 'credit_balance' && formData.amount && (
              <div style={{ marginTop: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)', paddingLeft: '26px' }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  Balance after: ₹{((liveBalance !== null ? liveBalance : parseFloat(currentUser?.creditBalance || 0)) - parseFloat(formData.amount)).toFixed(2)}
                </span>
              </div>
            )}
          </div>
          )}

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={gstIncluded}
                onChange={(e) => setGstIncluded(e.target.checked)}
                style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary)' }}
              />
              <span className="form-label" style={{ margin: 0 }}>Amount includes 18% GST</span>
            </label>
            {gstIncluded && formData.amount && (
              <div style={{
                marginTop: 'var(--spacing-xs)',
                padding: 'var(--spacing-sm)',
                backgroundColor: 'var(--color-gray-50)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-secondary)'
              }}>
                Base: {formatCurrency(parseFloat(formData.amount) / 1.18)} + GST: {formatCurrency(parseFloat(formData.amount) - parseFloat(formData.amount) / 1.18)} = Total: {formatCurrency(formData.amount)}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              name="notes"
              className="form-input"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Additional details..."
            />
          </div>

          <div className="flex gap-md">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex-center gap-sm">
                  <div className="spinner spinner-sm"></div>
                  {isRecurring ? 'Creating...' : 'Saving...'}
                </span>
              ) : (
                isRecurring ? 'Create Recurring Expense' : 'Save Expense'
              )}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceUpload;
